import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { OiPageFrame } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { automationService } from '@/services/automation.service';

const { Title, Paragraph, Text } = Typography;

type DraftNode = {
  key: string;
  nodeKey: string;
  nodeKind: string;
  title: string;
  next?: string;
  message?: string;
  minutes?: number;
};

const AutomationDesignerPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [nodes, setNodes] = useState<DraftNode[]>([
    {
      key: '1',
      nodeKey: 'notify_buyer',
      nodeKind: 'notify',
      title: 'Notify Buyer',
      next: 'wait_ack',
      message: 'Alert procurement',
    },
    {
      key: '2',
      nodeKey: 'wait_ack',
      nodeKind: 'wait_human',
      title: 'Await acknowledgement',
      message: 'Buyer confirms',
    },
  ]);

  const designerQ = useQuery({
    queryKey: ['eabp-designer'],
    queryFn: () => automationService.getDesigner(),
    staleTime: 60_000,
  });

  const createMut = useMutation({
    mutationFn: (values: any) => {
      const definition = {
        entryNodeKey: nodes[0]?.nodeKey,
        nodes: nodes.map((n, i) => ({
          nodeKey: n.nodeKey,
          nodeKind: n.nodeKind,
          title: n.title,
          sortOrder: i + 1,
          config:
            n.nodeKind === 'timer'
              ? { minutes: n.minutes || 15 }
              : { message: n.message || n.title },
          next: n.next || undefined,
        })),
      };
      return automationService.createPlaybook({
        code: values.code,
        title: values.title,
        description: values.description,
        domain: values.domain || 'operations',
        triggerEventType: values.triggerEventType,
        definition,
        publish: true,
      });
    },
    onSuccess: () => {
      message.success('Playbook published');
      void qc.invalidateQueries({ queryKey: ['eabp-designer'] });
      void qc.invalidateQueries({ queryKey: ['eabp-monitor'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const allowed = ((designerQ.data as any)?.allowedNodeKinds as string[]) || [];
  const forbidden = ((designerQ.data as any)?.forbiddenNodeKinds as string[]) || [];

  return (
    <OiPageFrame subtitle="Structured low-code automation designer. Forbidden kinds (post journal / approve payment/PO/HR) are rejected by the API.">
      <Space style={{ marginBottom: 12 }}>
        <Button onClick={() => navigate('/owner-intelligence/automation')}>Back to monitor</Button>
      </Space>
      <Title level={3} style={{ marginTop: 0 }}>
        Automation Designer
      </Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Enterprise GPS — orchestration only"
        description={`Allowed: ${allowed.join(', ') || '…'}. Blocked: ${forbidden.join(', ') || '…'}.`}
      />

      <Card title="Playbook header" style={{ marginBottom: 16 }}>
        <Form
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
          initialValues={{
            code: `pb_${Date.now().toString(36)}`,
            title: 'New coordinated playbook',
            triggerEventType: 'supplier_delay',
            domain: 'procurement',
          }}
        >
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="triggerEventType" label="Trigger event" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'supplier_delay', label: 'supplier_delay' },
                { value: 'invoice_overdue', label: 'invoice_overdue' },
                { value: 'machine_down', label: 'machine_down' },
                { value: 'inspection_failed', label: 'inspection_failed' },
              ]}
            />
          </Form.Item>
          <Form.Item name="domain" label="Domain">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Paragraph>
            <Text strong>Nodes</Text>
          </Paragraph>
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            dataSource={nodes}
            columns={[
              {
                title: 'Key',
                render: (_, r, idx) => (
                  <Input
                    value={r.nodeKey}
                    onChange={(e) => {
                      const next = [...nodes];
                      next[idx] = { ...r, nodeKey: e.target.value };
                      setNodes(next);
                    }}
                  />
                ),
              },
              {
                title: 'Kind',
                render: (_, r, idx) => (
                  <Select
                    style={{ width: 180 }}
                    value={r.nodeKind}
                    options={allowed.map((k) => ({ value: k, label: k }))}
                    onChange={(v) => {
                      const next = [...nodes];
                      next[idx] = { ...r, nodeKind: v };
                      setNodes(next);
                    }}
                  />
                ),
              },
              {
                title: 'Title',
                render: (_, r, idx) => (
                  <Input
                    value={r.title}
                    onChange={(e) => {
                      const next = [...nodes];
                      next[idx] = { ...r, title: e.target.value };
                      setNodes(next);
                    }}
                  />
                ),
              },
              {
                title: 'Next / msg',
                render: (_, r, idx) =>
                  r.nodeKind === 'timer' ? (
                    <InputNumber
                      min={1}
                      value={r.minutes || 15}
                      onChange={(v) => {
                        const next = [...nodes];
                        next[idx] = { ...r, minutes: Number(v) || 15 };
                        setNodes(next);
                      }}
                    />
                  ) : (
                    <Input
                      placeholder="next node key or message"
                      value={r.next || r.message}
                      onChange={(e) => {
                        const next = [...nodes];
                        next[idx] = {
                          ...r,
                          ...(r.nodeKind === 'notify' || r.nodeKind === 'wait_human'
                            ? { message: e.target.value }
                            : { next: e.target.value }),
                        };
                        setNodes(next);
                      }}
                    />
                  ),
              },
            ]}
          />
          <Space style={{ marginTop: 12 }}>
            <Button
              onClick={() =>
                setNodes((n) => [
                  ...n,
                  {
                    key: String(Date.now()),
                    nodeKey: `step_${n.length + 1}`,
                    nodeKind: 'notify',
                    title: 'New step',
                    message: 'Notify',
                  },
                ])
              }
            >
              Add node
            </Button>
            <Button type="primary" htmlType="submit" loading={createMut.isPending}>
              Publish playbook
            </Button>
          </Space>
        </Form>
      </Card>
    </OiPageFrame>
  );
};

export default AutomationDesignerPage;
